import { isSimpleError, isSimpleErrors, SimpleError } from '@simonbackx/simple-errors';
import { AccessRight } from '../AccessRight.js';
import { PermissionsResourceType } from '../PermissionsResourceType.js';
export class EventPermissionChecker {
    /**
     * Will throw error if not allowed to edit/add/delete this event
     * @param event
     * @returns Organization if event for specific organization, else null
     * @throws error if not allowed to write this event
     */
    static async checkEventAccessAsync(event, { getOrganization, userPermissions, platform, }) {
        if (!userPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qQ`),
                statusCode: 403,
            });
        }
        if (event.organizationId !== null) {
            let organization;
            try {
                organization = await getOrganization(event.organizationId);
            }
            catch (error) {
                throw new SimpleError({
                    code: 'not_found',
                    message: 'Organization not found',
                    human: $t(`%DY`),
                });
            }
            this.throwIfNoPermissionToWriteEventForOrganization(event, organization, platform, userPermissions);
            return organization;
        }
        this.throwIfNoPermissionToWriteNationalOrRegionalEvent(event, userPermissions, platform);
        return null;
    }
    /**
     * Will throw error if not allowed to edit/add/delete this event
     * @param event
     * @throws error if not allowed to write this event
     */
    static throwIfNoPermissionToWriteEvent(event, { organization, userPermissions, platform, }) {
        if (!userPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qQ`),
                statusCode: 403,
            });
        }
        if (event.organizationId !== null) {
            if (organization === null) {
                throw new Error('Organization not specified.');
            }
            this.throwIfNoPermissionToWriteEventForOrganization(event, organization, platform, userPermissions);
        }
        else {
            this.throwIfNoPermissionToWriteNationalOrRegionalEvent(event, userPermissions, platform);
        }
    }
    static hasPermissionToWriteEvent(event, options) {
        try {
            this.throwIfNoPermissionToWriteEvent(event, options);
        }
        catch (error) {
            if (isSimpleError(error) || isSimpleErrors(error)) {
                return false;
            }
            throw error;
        }
        return true;
    }
    static throwIfNoPermissionToWriteEventForOrganization(event, organization, platform, userPermissions) {
        const accessRight = AccessRight.EventWrite;
        if (event.organizationId !== organization.id) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qR`),
                statusCode: 403,
            });
        }
        const organizationPermissions = userPermissions.forOrganization(organization, platform);
        if (!organizationPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qS`),
                statusCode: 403,
            });
        }
        if (event.meta.groups === null) {
            if (!organizationPermissions.hasResourceAccessRight(PermissionsResourceType.Groups, '', accessRight)) {
                throw new SimpleError({
                    code: 'permission_denied',
                    message: $t(`%qS`),
                    statusCode: 403,
                });
            }
        }
        else {
            for (const group of event.meta.groups) {
                if (!organizationPermissions.hasResourceAccessRight(PermissionsResourceType.Groups, group.id, accessRight)) {
                    throw new SimpleError({
                        code: 'permission_denied',
                        message: $t(`%qT`),
                        statusCode: 403,
                    });
                }
            }
        }
    }
    static throwIfNoPermissionToWriteNationalOrRegionalEvent(event, userPermissions, platform) {
        const accessRight = AccessRight.EventWrite;
        const platformPermissions = userPermissions.forPlatform(platform);
        if (!platformPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qU`),
                statusCode: 403,
            });
        }
        // organization tags
        if (event.meta.organizationTagIds === null) {
            if (!(platformPermissions.hasResourceAccessRight(PermissionsResourceType.OrganizationTags, '', accessRight))) {
                throw new SimpleError({
                    code: 'permission_denied',
                    message: $t(`%qV`),
                    statusCode: 403,
                });
            }
        }
        else {
            for (const tagId of event.meta.organizationTagIds) {
                if (!platformPermissions.hasResourceAccessRight(PermissionsResourceType.OrganizationTags, tagId, accessRight)) {
                    throw new SimpleError({
                        code: 'permission_denied',
                        message: $t(`%qW`),
                        statusCode: 403,
                    });
                }
            }
        }
    }
}
//# sourceMappingURL=EventPermissionChecker.js.map